import { create } from 'zustand';

/**
 * pre186: Berater-Chat ist jetzt rein 1:1 und Ende-zu-Ende (P2P-DataChannel).
 * - Inhalte verlassen NIE den Server (auch nicht verschlüsselt) — Signaling-Relay only.
 * - Verlauf liegt ausschließlich im RAM (kein localStorage/IndexedDB; geteilte Workstations).
 *   Ein Reload/Logout löscht den Verlauf bewusst.
 */
export interface ChatMsg { id: string; body: string; ts: number; mine: boolean }
export type PeerStatus = 'idle' | 'connecting' | 'open' | 'closed';
export interface Unread { total: number; scopes: Record<string, number> }

interface ChatState {
  open: boolean;
  peerId: string | null;        // aktiver 1:1-Partner (null → Liste)
  peerName: string;
  threads: Record<string, ChatMsg[]>;   // peerId → Nachrichten (RAM)
  names: Record<string, string>;        // peerId → Anzeigename (RAM)
  status: Record<string, PeerStatus>;   // peerId → Verbindungszustand
  unread: Unread;                       // scopes keyed by peerId
  openChat: (peerId: string, name: string) => void;
  toggle: () => void;
  showList: () => void;
  close: () => void;
  addMessage: (peerId: string, name: string, msg: ChatMsg) => void;
  setStatus: (peerId: string, st: PeerStatus) => void;
  markRead: (peerId: string) => void;
}

const recompute = (scopes: Record<string, number>): Unread => ({
  total: Object.values(scopes).reduce((a, b) => a + b, 0), scopes,
});

export const useChatStore = create<ChatState>((set, get) => ({
  open: false,
  peerId: null,
  peerName: '',
  threads: {},
  names: {},
  status: {},
  unread: { total: 0, scopes: {} },

  openChat: (peerId, name) => {
    const scopes = { ...get().unread.scopes }; delete scopes[peerId];
    set((s) => ({ open: true, peerId, peerName: name, names: { ...s.names, [peerId]: name }, unread: recompute(scopes) }));
  },
  toggle: () => set((s) => ({ open: !s.open })),
  showList: () => set({ open: true, peerId: null, peerName: '' }),
  close: () => set({ open: false }),

  addMessage: (peerId, name, msg) => set((s) => {
    const thread = s.threads[peerId] ? [...s.threads[peerId]] : [];
    if (thread.some((m) => m.id === msg.id)) return {};
    thread.push(msg);
    const isActive = s.open && s.peerId === peerId;
    const scopes = { ...s.unread.scopes };
    if (!msg.mine && !isActive) scopes[peerId] = (scopes[peerId] ?? 0) + 1;
    return {
      threads: { ...s.threads, [peerId]: thread },
      names: { ...s.names, [peerId]: name || s.names[peerId] || 'Berater' },
      unread: recompute(scopes),
    };
  }),

  setStatus: (peerId, st) => set((s) => ({ status: { ...s.status, [peerId]: st } })),

  markRead: (peerId) => set((s) => {
    const scopes = { ...s.unread.scopes }; delete scopes[peerId];
    return { unread: recompute(scopes) };
  }),
}));
